/* eslint-disable @typescript-eslint/no-explicit-any */
import { Plus, Megaphone, Trash2, Power, Edit2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import Modal from '../model/Modal';
import AddsForm, { type FormData } from '../ads/AddsForm';
import apiClient from '../../api/client';
import { toast } from 'sonner';
import ConfirmModal from '../model/ConfirmModal';

interface Ad{
  id?: string;
  title?: string;
  image?: string;
  isActive?: boolean;
  url?: string;
  description?: string;
}

export default function AdManager() {
  const [ads, setAds] = useState<Ad[] | []>([]);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingAdds, setEditingAdds] = useState(false);
  const [editingData, setEditingData] = useState<FormData>({
    id: "",
    title: "",
    image: null,
    status: true,
    url: "",
    description: "",
  })
  const [confirmOpen, setConfirmopen] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const toggleStatus =async(id?: string, status?: boolean) => {
    try{
      const {data} = await apiClient.patch(`/admin/ads/${id}`, {isActive: !(status)}, { headers: {'Content-Type': 'multipart/form-data'}})
          if(data.success){
            const updatedAd = data.ads;

            setAds(prev =>
              (prev ?? []).map(ad =>
                ad?.id === updatedAd?.id ? updatedAd : ad
              )
            );
          }
    }
    catch(error: any){
      toast.error(error?.response?.data?.message || error?.message)
    }
  };

  const handleDelete = (id?: string) => {
    if(id){
      setSelectedId(id);
      setConfirmopen(true);
    }
  };

  const confirmDelete = async () => {
    try {
      const { data } = await apiClient.patch(`/admin/ads/delete/${selectedId}`);
      
      if (data.success) {
        setAds(prev => (prev ?? []).filter(ad => ad?.id !== selectedId));
        toast.success('Ad deleted successfully');
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message);
    }
  };

  const handleSubmit=async(fields: FormData)=>{
    if(!editingAdds){
      try{
          const formData = new FormData();
          formData.append('title', fields?.title ?? "");
          formData.append('url', fields?.url ?? "");
          formData.append('description', fields?.description ?? "");
          formData.append('isActive', String(fields.status));

          if (fields.image instanceof File) {
            formData.append('image', fields.image);
          }

          const {data} = await apiClient.post(`/admin/ads`, formData, { headers: {'Content-Type': 'multipart/form-data'}})

          if(data.success){
            toast.success(data?.message || "Ad created successfully")
            setIsFormModalOpen(false);
            setEditingData({id: "", title: "", image: null, status: true, url: "", description: ""})
            fetchAds()
          }
      }
      catch(error: any){
        toast.error(error?.response?.data?.message || error?.message)
      }
    }
    else{
      try{
          const formData = new FormData();
          formData.append('title', fields?.title ?? "");
          formData.append('url', fields?.url ?? "");
          formData.append('description', fields?.description ?? "");
          formData.append('isActive', String(fields.status));

          if (fields.image instanceof File) {
            formData.append('image', fields.image);
          }

          const {data} = await apiClient.patch(`/admin/ads/${fields?.id}`, formData, { headers: {'Content-Type': 'multipart/form-data'}})

          if(data.success){
            toast.success(data?.message || "Ad edited successfully")

            const updatedAd = data.ads;

            setAds(prev =>
              (prev ?? []).map(ad =>
                ad?.id === updatedAd?.id ? updatedAd : ad
              )
            );

            setIsFormModalOpen(false);
            setEditingData({id: "", title: "", image: null, status: true, url: "", description: ""})
          }
      }
      catch(error: any){
        toast.error(error?.response?.data?.message || error?.message)
      }

    }
  }

  const fetchAds=async()=>{
        try{
          const {data} = await apiClient.get(`/admin/ads/notActive`)

          if(data.success){
            setAds(data?.ads)
          }
      }
      catch(error: any){
        toast.error(error?.response?.data?.message || error?.message)
      }
  }

  useEffect(()=>{
    (async()=>{
      setLoading(true)
      await fetchAds()
      setLoading(false)
    })()
  },[])

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Advertisement Manager</h1>
          <p className="text-slate-400 mt-1">Manage banner ads and sponsorships.</p>
        </div>
        <button 
            className="flex items-center gap-2 bg-cyan-600 text-white px-5 py-2.5 rounded-xl hover:bg-cyan-500 transition-all shadow-lg shadow-cyan-900/20"
            onClick={()=>{setIsFormModalOpen(true); setEditingAdds(false)}}
        >
          <Plus className="w-4 h-4" />
          New Campaign
        </button>
      </div>

      {loading ? <BoxSkeleton /> :
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ads?.map((ad) => (
            <div key={ad.id} className="bg-[#0B1623] border border-white/10 rounded-2xl overflow-hidden hover:border-cyan-500/30 transition-all group">
              {/* <div className={`h-32 w-full relative`}> */}
                {/* <div
                  className="h-32 w-full relative bg-center bg-cover bg-no-repeat"
                  style={{ backgroundImage: `url(${ad.image})` }}
                >
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <Megaphone className="w-8 h-8 text-white/50" />
                </div>
                <div className="absolute top-3 right-3">
                  <span className={`px-2 py-1 rounded-lg text-xs font-bold uppercase tracking-wider ${
                    ad.isActive ? 'bg-green-500 text-white' : 'bg-slate-500 text-white'
                  }`}>
                    {ad.isActive ? 'Active' : 'Paused'}
                  </span>
                </div>
              </div> */}

              <div className="w-full aspect-video relative rounded-xl overflow-hidden">
                <img
                  src={ad.image}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover blur-md scale-105 opacity-30"
                />

                <div className="absolute inset-0 flex items-center justify-center bg-black/25">
                  <img
                    src={ad.image}
                    alt=""
                    className="w-full h-full object-contain"
                  />
                </div>

                <div className="absolute inset-0 flex items-center justify-center">
                  <Megaphone className="w-8 h-8 text-white/60" />
                </div>

                <div className="absolute top-3 right-3">
                  <span
                    className={`px-2 py-1 rounded-lg text-xs font-bold uppercase tracking-wider ${
                      ad.isActive ? "bg-green-500 text-white" : "bg-slate-500 text-white"
                    }`}
                  >
                    {ad.isActive ? "Active" : "Paused"}
                  </span>
                </div>
              </div>
              
              <div className="p-6">
                <h3 className="font-bold text-white text-lg mb-4">{ad.title}</h3>
  {/*               
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-white/5 rounded-xl p-3">
                    <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                      <Eye className="w-3 h-3" /> Views
                    </div>
                    <p className="text-white font-semibold text-lg">{ad.views}</p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3">
                    <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                      <MousePointer2 className="w-3 h-3" /> Clicks
                    </div>
                    <p className="text-white font-semibold text-lg">{ad.clicks}</p>
                  </div>
                </div> */}

                <div className="flex gap-2">
                  <button 
                    onClick={() => toggleStatus(ad.id, ad.isActive)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium transition-all ${
                      ad.isActive
                        ? 'bg-white/5 text-slate-300 hover:bg-white/10' 
                        : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                    }`}
                  >
                    <Power className="w-4 h-4" /> {ad.isActive ? 'Pause' : 'Activate'}
                  </button>

                  <button 
                    onClick={() => {setIsFormModalOpen(true); setEditingAdds(true); setEditingData({id: String(ad.id), image: ad.image, title: ad.title, status: ad.isActive, url: ad.url, description: ad.description})}}
                    className="p-2.5 rounded-xl bg-green-500/10 text-green-400 hover:bg-green-500/20 transition-all"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>

                  <button 
                    onClick={() => handleDelete(ad?.id)}
                    className="p-2.5 rounded-xl bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      }

      { !loading && ads.length == 0 &&
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <p className="text-sm text-slate-400">No advertisements found.</p>
        </div>
      }

      <Modal
        isOpen={isFormModalOpen}
        onClose={() => {
          setIsFormModalOpen(false);
          setEditingData({id: "", title: "", image: null, status: true, url: "", description: ""})}}
        title={editingAdds ? 'Edit Schedule' : 'Add New Schedule'}
      >
        <AddsForm 
          onSubmit={handleSubmit}
          initialData={{
            id: editingData.id,
            title: editingData.title,
            image: editingData.image,
            status: editingData.status,
            url: editingData.url,
            description: editingData.description,
          }}
        />
      </Modal>

      <ConfirmModal
        isOpen={confirmOpen}
        onClose={() => setConfirmopen(false)}
        onConfirm={confirmDelete}
        title="Delete Ad"
        message="Are you sure you want to delete this ad? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isDestructive={true}
      />
    </div>
  );
}

export function BoxSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="aspect-square rounded-lg bg-gray-500 animate-pulse"
        />
      ))}
    </div>
  );
}

